<template>
  <div class="widget w-mini-markets-widget">
    <c-widget-header hideMenu hideLinkMode :wid="wid">
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar>
      <v-form-group label="">
        <v-form-select
          :value="selectedMarket"
          :options="marketOptions"
          @input="onMarketSelected"
          :aria-label="$t('white-label.widgets.markets.controlbar.market.label')"
        />
      </v-form-group>
    </c-widget-control-bar>

    <div class="widget-body">
      <ag-grid-vue
        v-if="!isError"
        class="c-data-grid ag-theme-balham-dark"
        animateRows
        suppressContextMenu
        immutableData
        :asyncTransactionWaitMillis="gridUpdateInterval"
        :suppressFieldDotNotation="true"
        :gridOptions="gridOptions"
        :defaultColDef="defaultColumnDef"
        :columnDefs="columnDefs"
        :context="this"
        :rowHeight="agGridRowHeight"
        rowSelection="single"
        ensureDomOrder
        suppressDragLeaveHidesColumns
        :suppressColumnVirtualisation="isAccessibilityEnabled"
        @grid-ready="onGridReady"
        @cell-focused="onCellFocused"
        @async-transactions-flushed="onAsyncTransactionsFlushed"
        @column-resized="onColResized"
      />
    </div>
  </div>
</template>

<script>
import agGridCommons from '@mixins/agGridCommons';
import indicesSectorsList from '@/utils/indicesSectorsList.json';
import sectorsIdsMap from '@/utils/sectorsIdsMap.json';

import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';

import { RowButtonRenderer } from '@custom/dataGrid/renderers';

import { API } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';

export default {
  name: 'w-mini-markets-widget',
  mixins: [agGridCommons],
  components: {
    CWidgetHeader,
    CWidgetHeaderButton,
    CWidgetControlBar,
    AgGridVue,
    RowButtonRenderer
  },
  props: {
    isSidebar: Boolean,
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isError: false,
      marketOptions: [
        { value: 'all_indices', text: 'All Indices' },
        { value: 'ca_indices', text: 'Canadian Indices' },
        { value: 'us_indices', text: 'US Indices' },
        { value: 'ca_sectors', text: 'Canadian Sectors' },
        { value: 'us_sectors', text: 'US Sectors' }
      ],
      selectedMarket: '',
      subscriptionId: null,
      gridOptions: {
        getRowNodeId(data) {
          return data['q.RIC'];
        },
        columnTypes: { actionable: {} }
      },
      tableFieldsPrepend: ['action']
    };
  },
  watch: {
    market: {
      handler(val) {
        this.selectedMarket = val;
        this.fetch();
      },
      immediate: true
    }
  },
  computed: {
    market() {
      return this.$store.state.widget[this.wid].market;
    },
    extendedColDefs() {
      return {
        'q.RIC': {
          hide: true
        },
        action: {
          cellRenderer: 'RowButtonRenderer',
          width: 25,
          resizable: false
        },
        'q._TCR': {
          width: 60
        },
        'q._TRDPRC_1': {
          width: 80
        },
        'q._PCTCHNG': {
          width: 65
        }
      };
    },
    rics() {
      switch (this.selectedMarket) {
        case 'all_indices':
          return indicesSectorsList.indices.CA.concat(indicesSectorsList.indices.US).map(index => index.value);
        case 'ca_indices':
          return indicesSectorsList.indices.CA.map(index => index.value);
        case 'us_indices':
          return indicesSectorsList.indices.US.map(index => index.value);
        case 'ca_sectors':
          return indicesSectorsList.sectors.CA.map(sector => sector.value);
        case 'us_sectors':
          return indicesSectorsList.sectors.US.map(sector => sector.value);
      }
    },
    identifier() {
      return `${this.name}-${this._uid}`;
    }
  },
  methods: {
    onMarketSelected(market) {
      this.$store.commit(`widget/${this.wid}/MARKET_CHANGED`, market);
    },
    getQuoteData(rics) {
      return API.Rest.get(`/quote/info?rics=${rics}&fids=${this.requestFids}`).then(response => {
        // if (response.data.data.length === 0) {
        //   throw new Error('NO_QUOTE_INFO_RECEIVED');
        // }
        Object.assign(
          this.formats,
          Object.keys(response.data.metadata).reduce((acc, ric) => {
            acc[ric] = response.data.metadata[ric].type;
            return acc;
          }, {})
        );
        // reset net change collection to prevent holding obsolete values
        this.netChanges = {};

        const snapshot = response.data.data;
        const rowsData = [];
        snapshot.forEach(data => {
          if (isNaN(data['rkd.COMP_TAXONOMY_TRBC_CD_L1']) && sectorsIdsMap[data['q.RIC']])
            data['rkd.COMP_TAXONOMY_TRBC_CD_L1'] = sectorsIdsMap[data['q.RIC']];
          const rowData = this.hydrateData(data);
          rowsData.push(rowData);
        });

        this.gridOptions.api.setRowData(rowsData);
      });
    },
    handleUpdate(response, error) {
      if (error) {
        console.error('Streaming update', error);
        return;
      }
      response = response.payload[0];
      let rowNode;
      try {
        rowNode = this.gridOptions.api.getRowNode(response.ric);
      } catch (e) {
        return;
      }
      // if there's no rowNode, then there's most probalby a running refetch, so don't try to update removed rows
      if (!rowNode) return;
      const updateData = this.hydrateData(response.data, rowNode.data);
      if (this.gridOptions && this.gridOptions.api)
        this.gridOptions.api.applyTransactionAsync({ update: [updateData] });
    },

    fetch() {
      this.streamingUnsubscribe();
      // reset table on re-fetch to avoid cell renderers to attempt rendering before the data arrives
      if (this.gridOptions.api) this.gridOptions.api.setRowData([]);
      this.getQuoteData(this.rics)
        .then(() => {
          this.streamingSubscribe(this.rics);
        })
        .catch(error => {
          console.warn(error);
          this.isError = true;
        });
    },
    onGridReady(ev) {
      this.onTableReady(ev, this.$t('white-label.widgets.miniMarkets.widget-title'));
    }
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>
<style lang="scss">
@import '~@/styles/widgets/mini-markets-widget';
</style>
